import { onlineManager } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';

import { refreshSession } from '@/shared/api/client';

import { captureAuthLifetime, getAccessToken, isAuthLifetimeCurrent, useAuthStore } from './auth.store';

/**
 * Network attempts for the boot refresh.  With backoff (1s, 2s, 4s, 8s, 16s,
 * or the server's Retry-After) this rides out a deploy's ~30s of 502s or a
 * brief rate limit instead of bouncing a perfectly valid session to /login.
 */
export const BOOT_REFRESH_ATTEMPTS = 6;

/** Pause between boot refresh rounds while a remembered user's refresh stays inconclusive. */
export const BOOT_RETRY_MS = 10_000;

/**
 * Boot the session on page load.
 *
 * The access token lives in memory only (the refresh token is an httpOnly
 * cookie JS can't read), so after a reload there's no token until we re-mint
 * one.  This runs a silent `POST /api/auth/refresh` against the cookie, then
 * flips `bootstrapped` so the auth-guard knows whether to render the app or
 * bounce to /login.  Without this gate, the guard would redirect every reload
 * before the refresh had a chance to restore the session.
 *
 * Only a definitive 401/403 ends the session; 429 / 5xx / network errors are
 * retried first (see `refreshSession`).  Tabs restored together (Safari
 * session restore, laptop wake) coordinate through a Web Lock + broadcast, so
 * one tab rotates the refresh token and the others reuse its access token.
 *
 * Returns the current `bootstrapped` flag.
 */
export function useAuthBootstrap(): boolean {
  const bootstrapped = useAuthStore((s) => s.bootstrapped);

  useEffect(() => {
    if (bootstrapped) return;
    let cancelled = false;
    void (async () => {
      if (!getAccessToken()) {
        if (isOfflineColdLaunch()) {
          // Airplane mode with a remembered user: open the app from the offline
          // cache right away (My Work, Inbox) instead of waiting ~30s on a
          // refresh that can't succeed, then renew once the network is back.
          renewSessionWhenOnline();
        } else {
          let outcome = await refreshSession({ attempts: BOOT_REFRESH_ATTEMPTS });
          // Still inconclusive (API down past the backoff, e.g. a long deploy,
          // or rate-limited) but this browser remembers a user: the cookie may
          // well be valid, so keep the boot screen and keep trying rather than
          // bouncing to /login.  Offline → the deferred (cached) session.
          while (!cancelled && outcome === 'unavailable' && useAuthStore.getState().user && !getAccessToken()) {
            if (!onlineManager.isOnline()) {
              renewSessionWhenOnline();
              break;
            }
            await new Promise((resolve) => setTimeout(resolve, BOOT_RETRY_MS));
            if (cancelled) return;
            outcome = await refreshSession({ attempts: BOOT_REFRESH_ATTEMPTS });
          }
        }
      }
      if (!cancelled) useAuthStore.getState().setBootstrapped();
    })();
    return () => {
      cancelled = true;
    };
  }, [bootstrapped]);

  return bootstrapped;
}

/** Launched with no network and a remembered (non-secret) user snapshot. */
function isOfflineColdLaunch(): boolean {
  return !onlineManager.isOnline() && !!useAuthStore.getState().user;
}

/** Retry cadence while online but the refresh is still `unavailable` (5xx / 429). */
const RENEW_RETRY_MS = 30_000;

// ── Deferred session (offline cold launch) ──────────────────────────────────
// True from an offline cold launch until the session is renewed (or proven
// gone). While true, the auth-guard keeps the shell up on the remembered user
// instead of bouncing to /login — including the reconnect window, when the
// network is back but the refresh hasn't answered yet.
let deferred = false;
const deferredListeners = new Set<() => void>();

function setDeferred(next: boolean): void {
  if (deferred === next) return;
  deferred = next;
  for (const listener of deferredListeners) listener();
}

function subscribeDeferred(listener: () => void): () => void {
  deferredListeners.add(listener);
  return () => deferredListeners.delete(listener);
}

/** Whether the session is deferred by an offline cold launch (see above). */
export function useSessionDeferred(): boolean {
  return useSyncExternalStore(
    subscribeDeferred,
    () => deferred,
    () => false,
  );
}

/**
 * Renew the session from the refresh cookie as soon as React Query sees the
 * network again. A definitive 401/403 ends the session (the auth-guard then
 * sends the user to /login); a 5xx / 429 keeps the cached view and retries.
 */
function renewSessionWhenOnline(): void {
  setDeferred(true);
  const lifetime = captureAuthLifetime();
  const attempt = () => {
    if (!isAuthLifetimeCurrent(lifetime)) { setDeferred(false); return; }
    void refreshSession({ attempts: BOOT_REFRESH_ATTEMPTS }).then((outcome) => {
      if (!isAuthLifetimeCurrent(lifetime)) { setDeferred(false); return; }
      if (outcome === 'unavailable') {
        setTimeout(() => (onlineManager.isOnline() ? attempt() : waitForNetwork()), RENEW_RETRY_MS);
        return;
      }
      if (outcome === 'logged_out') useAuthStore.getState().clear();
      setDeferred(false);
    });
  };
  const waitForNetwork = () => {
    const unsubscribe = onlineManager.subscribe((online) => {
      if (!online) return;
      unsubscribe();
      attempt();
    });
  };
  waitForNetwork();
}
